"use client";
import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import * as React from "react";
import { cn } from "@/util/string";

/**
 * A provider to group multiple tooltips and share a delay.
 */
export const TooltipProvider = BaseTooltip.Provider;

/**
 * The root component of the tooltip contains the trigger and the popup.
 */
export const Tooltip = BaseTooltip.Root;

/**
 * The element that triggers the tooltip.
 */
export const TooltipTrigger = BaseTooltip.Trigger;

function ArrowSvg(props: React.ComponentProps<"svg">) {
	return (
		// biome-ignore lint/a11y/noSvgWithoutTitle: Tooltip arrow
		<svg width="20" height="10" viewBox="0 0 20 10" fill="none" {...props}>
			<path
				d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
				className="fill-base-100"
			/>
			<path
				d="M8.99542 1.85876C9.75604 1.17425 10.9106 1.17422 11.6713 1.85878L16.5281 6.22989C17.0789 6.72568 17.7938 7.00001 18.5349 7.00001L15.89 7L11.0023 2.60207C10.622 2.2598 10.0447 2.2598 9.66436 2.60207L4.77734 7L2.13171 7.00001C2.87284 7.00001 3.58774 6.72568 4.13861 6.22989L8.99542 1.85876Z"
				className="fill-base-200"
			/>
		</svg>
	);
}

export type TooltipContentProps = React.ComponentPropsWithoutRef<
	typeof BaseTooltip.Popup
> & {
	sideOffset?: number;
	showArrow?: boolean;
};

/**
 * The content of the tooltip. Includes the Portal, Positioner, Popup, and Arrow.
 */
export const TooltipContent = React.forwardRef<
	HTMLDivElement,
	TooltipContentProps
>(
	(
		{ className, sideOffset = 8, showArrow = true, children, ...props },
		ref,
	) => {
		return (
			<BaseTooltip.Portal>
				<BaseTooltip.Positioner sideOffset={sideOffset}>
					<BaseTooltip.Popup
						ref={ref}
						className={cn(
							"flex flex-col px-3 py-2 rounded-md bg-base-100 text-sm origin-(--transform-origin) shadow-lg border border-base-200 transition-[transform,scale,opacity] data-ending-style:opacity-0 data-ending-style:scale-90 data-instant:transition-none data-starting-style:opacity-0 data-starting-style:scale-90 text-base-content",
							className,
						)}
						{...props}
					>
						{showArrow && (
							<BaseTooltip.Arrow className="flex data-[side=bottom]:-top-2 data-[side=bottom]:rotate-0 data-[side=left]:-right-3.25 data-[side=left]:rotate-90 data-[side=right]:-left-3.25 data-[side=right]:-rotate-90 data-[side=top]:-bottom-2 data-[side=top]:rotate-180">
								<ArrowSvg />
							</BaseTooltip.Arrow>
						)}
						{children}
					</BaseTooltip.Popup>
				</BaseTooltip.Positioner>
			</BaseTooltip.Portal>
		);
	},
);
TooltipContent.displayName = "TooltipContent";
